import { Button, Result, Stack } from '@pypestream/design-system';
import React from 'react';
import type { useRetryableListLoad } from '../../hooks/use-retryable-list-load';
import { listLoadAnnouncementText } from '../../utils/list-load-view';
import { Loader } from '../loader';

/**
 * The visually hidden `role="status"` region of a retryable list page
 * (PSTUDIO-4177, PSTUDIO-4442; WCAG 4.1.3).
 *
 * The error panel replaces the loader without moving focus, and `ps-result`
 * renders a plain container — no role, no aria-live — so its arrival is silent
 * to a screen reader. This region is mounted for every view and only its text
 * changes, because a live region inserted together with its content is not
 * announced by every assistive technology.
 *
 * LIMITATION: that only holds while the page stays mounted. Arriving on a route
 * whose machine is ALREADY parked in `error` puts the region and its text into
 * the DOM in the same commit, and the announcement may be missed. The machine
 * also raises a toast on the failure (`onErrorActions` -> `sendUserMessage`),
 * which react-toastify renders with `role="alert"`, so a failure is announced
 * twice, once assertively with the raw error text and once politely here.
 *
 * The literals are the standard clip-rect idiom, not design values.
 */
export const ListLoadLiveRegion: React.FC<{
  text: string;
  testId: string;
}> = ({ text, testId }) => (
  <div
    role="status"
    aria-live="polite"
    data-testid={testId}
    style={{
      position: 'absolute',
      width: '1px',
      height: '1px',
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
    }}
  >
    {text}
  </div>
);

/**
 * The panel a failed list load shows in place of the table, with the Try again
 * button that sends the region's load event. While `busy` the button stays
 * mounted (its `loading` state) so focus is not dropped to <body>.
 */
export const ListLoadError: React.FC<{
  title: string;
  body: string;
  retryLabel: string;
  busy: boolean;
  onRetry: () => void;
  testId: string;
  retryTestId: string;
}> = ({ title, body, retryLabel, busy, onRetry, testId, retryTestId }) => (
  <Result
    align="center"
    status="error"
    test-id={testId}
    label={title}
    body={body}
  >
    <Stack gutter="xsmall" justifyContent="center" slot="footer">
      <Button
        size="large"
        test-id={retryTestId}
        loading={busy}
        onClick={onRetry}
      >
        {retryLabel}
      </Button>
    </Stack>
  </Result>
);

/**
 * Everything a retryable list page shows before its table: the live region
 * (always mounted), the loader, and the error panel, driven by the result of
 * `useRetryableListLoad`. The page renders its table itself, on
 * `view === 'ready'`.
 *
 * Test ids are `<testIdPrefix>-live-region`, `<testIdPrefix>-load-error` and
 * `<testIdPrefix>-retry-btn`.
 */
export const ListLoadStatus: React.FC<{
  load: Pick<
    ReturnType<typeof useRetryableListLoad>,
    'showLoader' | 'showError' | 'busyRetry' | 'announcement' | 'retry'
  >;
  texts: {
    title: string;
    body: string;
    retry: string;
    retrying: string;
    recovered: string;
  };
  testIdPrefix: string;
}> = ({ load, texts, testIdPrefix }) => (
  <>
    <ListLoadLiveRegion
      text={listLoadAnnouncementText(load.announcement, texts)}
      testId={`${testIdPrefix}-live-region`}
    />
    {load.showLoader && (
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader relative />
      </div>
    )}
    {load.showError && (
      <ListLoadError
        title={texts.title}
        body={texts.body}
        retryLabel={texts.retry}
        busy={load.busyRetry}
        onRetry={load.retry}
        testId={`${testIdPrefix}-load-error`}
        retryTestId={`${testIdPrefix}-retry-btn`}
      />
    )}
  </>
);
